import { IDataSet } from "../../models/data/data-set"
import { createCasesNotification } from "../../models/data/data-set-notifications"
import { toV2Id, toV3ItemId } from "../../utilities/codap-utils"
import { registerDIHandler } from "../data-interactive-handler"
import { DIHandler, DIHandlerFnResult, DIResources, DIValues } from "../data-interactive-types"
import { DIItem, DIItemValues, DINewCase } from "../data-interactive-data-set-types"
import { deleteItem, getItem, updateCaseBy, updateCasesBy } from "./handler-functions"
import { dataContextNotFoundResult, valuesRequiredResult } from "./di-results"

/**
 * Create the items of all segments in a single batched model change (one addCases, one
 * validateCases, one notification batch) and return one result per segment, equivalent to
 * processing each segment as its own create request. Used by the request coalescer to merge
 * consecutive streamed single-item create requests (CODAP-1408); the item handler's `create`
 * is the single-segment case.
 */
export function createItemsInSegments(dataContext: IDataSet, segments: DIItemValues[][]): DIHandlerFnResult[] {
  // Normalize all segments' items into one flat array, remembering each segment's range
  // (used both to slice itemIDs per segment and to map each item back to its segment below).
  const items: DIItem[] = []
  const segmentRanges: Array<{ start: number, count: number }> = []
  segments.forEach(segment => {
    const start = items.length
    // Some plugins (Collaborative) create items with values like [{ values: { ... } }] instead of
    // like [{ ... }], so we accommodate that extra layer of indirection here. Only a non-null,
    // non-array object is the wrapper shape: guard against null (typeof null === "object") and
    // against an array so a malformed request can't crash on the __id__ access below.
    segment.forEach(item => {
      const wrapped = item.values
      const newItem: DIItem =
        wrapped != null && typeof wrapped === "object" && !Array.isArray(wrapped)
          ? wrapped as DIItem
          : item

      // If an id is specified, we need to put it in the right format
      // The Collaborative plugin makes use of this feature
      const { id } = item
      if (!newItem.__id__ && (typeof id === "string" || typeof id === "number")) {
        newItem.__id__ = toV3ItemId(id)
      }
      items.push(newItem)
    })
    segmentRanges.push({ start, count: items.length - start })
  })

  const newCaseIds: Record<string, string[]> = {}
  let itemIDs: string[] = []

  dataContext.applyModelChange(() => {
    // Bring grouping up to date before adding, so the add can group the new items additively
    // and say which cases it created. Left invalid, it would defer to a full rebuild that
    // knows only the end state, and the request would report nothing while cases existed.
    // Ordinary plugin traffic arrives here invalid: deleteItem, deleteCaseBy and
    // itemSearch.delete all removeCases without revalidating.
    dataContext.validateCases()

    // Add items and update cases. A multi-segment batch is a coalesced run of streamed
    // create requests, so observers (e.g. graphs) should snap rather than animate; a
    // single create request — even one with many items — animates as an ordinary add.
    const suppressAnimation = segments.length > 1
    itemIDs = dataContext.addCases(items, { canonicalize: true, suppressAnimation })
    dataContext.validateCases()

    // The cases this request created, as reported by the add itself. The additive path knows
    // exactly which case ids it minted; the paths that regroup work it out by comparison.
    const created = dataContext.takeCaseIdsCreatedByLastAppend() ?? {}
    dataContext.collections.forEach(collection => {
      // Report them in the collection's case order, as a scan of its case ids would. Cases
      // with no index of their own (hidden ones) aren't in that list and aren't reported.
      newCaseIds[collection.id] = (created[collection.id] ?? [])
        .map(caseId => ({ caseId, index: collection.getCaseIndex(caseId) ?? -1 }))
        .filter(({ index }) => index >= 0)
        .sort((a, b) => a.index - b.index)
        .map(({ caseId }) => caseId)
    })
  }, {
    notify: () => {
      const notifications = []
      for (const collectionId in newCaseIds) {
        const caseIds = newCaseIds[collectionId]
        if (caseIds.length > 0) {
          notifications.push(createCasesNotification(caseIds, dataContext))
        }
      }
      return notifications
    }
  })

  // Attribute each new case to the segment containing its earliest contributing item.
  // This matches sequential semantics: under one-create-at-a-time processing, a new
  // parent case is reported by the first request whose item forms it; later requests
  // join the existing case without reporting it. Items were appended in segment order, so
  // a case's earliest contributing item is simply the one in its lowest-numbered segment.
  const segmentCaseIDs: number[][] = segmentRanges.map(() => [])
  if (segments.length > 1) {
    const segmentByItemId = new Map<string, number>()
    segmentRanges.forEach(({ start, count }, segmentIndex) => {
      for (let i = start; i < start + count; ++i) segmentByItemId.set(itemIDs[i], segmentIndex)
    })
    for (const collectionId in newCaseIds) {
      newCaseIds[collectionId].forEach(caseId => {
        const childItemIds = dataContext.caseInfoMap.get(caseId)?.childItemIds ?? []
        const earliestSegment = childItemIds.reduce<number>((earliest, itemId) => {
          const segment = segmentByItemId.get(itemId)
          return segment != null && segment < earliest ? segment : earliest
        }, Infinity)
        // a new case with no contributing batch item shouldn't occur during pure adds;
        // attribute to the first segment if it ever does
        segmentCaseIDs[isFinite(earliestSegment) ? earliestSegment : 0].push(toV2Id(caseId))
      })
    }
  } else {
    // Single segment (the ordinary, non-coalesced create path): every new case trivially
    // belongs to segment 0, so skip the per-item attribution map and reduce entirely.
    for (const collectionId in newCaseIds) {
      newCaseIds[collectionId].forEach(caseId => segmentCaseIDs[0].push(toV2Id(caseId)))
    }
  }

  return segmentRanges.map(({ start, count }, index) => ({
    success: true as const,
    caseIDs: segmentCaseIDs[index],
    itemIDs: itemIDs.slice(start, start + count).map(itemID => toV2Id(itemID))
  }))
}

export const diItemHandler: DIHandler = {
  create(resources: DIResources, values?: DIValues) {
    const { dataContext } = resources
    if (!dataContext) return dataContextNotFoundResult
    if (!values) return valuesRequiredResult

    const items = (Array.isArray(values) ? values : [values]) as DIItemValues[]
    return createItemsInSegments(dataContext, [items])[0]
  },

  delete(resources: DIResources, values?: DIValues) {
    const { item } = resources

    let itemIds: string[] | undefined
    if (!item && values && Array.isArray(values)) {
      itemIds = (values as DINewCase[]).map(aCase => aCase.id != null && toV3ItemId(aCase.id))
        .filter(id => !!id) as string[]
    }

    return deleteItem(resources, item ?? itemIds)
  },

  get(resources: DIResources) {
    const { item } = resources

    return getItem(resources, item)
  },

  update(resources: DIResources, values?: DIValues) {
    const { item } = resources

    if (item) {
      return updateCaseBy(resources, values, item, { itemReturnStyle: true, resourceName: "item" })
    } else {
      return updateCasesBy(resources, values, true)
    }
  }
}

registerDIHandler("item", diItemHandler)
